import { useState } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { X, Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SignaturePad from './SignaturePad';
import TrimbleAddressAutocomplete from '@/components/TrimbleAddressAutocomplete';
import { jsPDF } from 'jspdf';

const inputCls = "w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50";
const labelCls = "text-xs text-muted-foreground block mb-1";
const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

const RATING_ITEMS = [
  'The pre-trip inspection (As required by Sec. 392.7)',
  'Coupling and uncoupling of combination units. (If applicable)',
  'Familiar with vehicle\'s controls',
  'Placing the vehicle in operation',
  'Use of vehicle\'s controls',
  'Use of vehicle\'s emergency equipment',
  'Operating the vehicle in traffic',
  'Operating the vehicle while passing other vehicles',
  'Turning the vehicle',
  'Braking, and slowing the vehicle by means other than braking.',
  'Backing the vehicle',
  'Parking the vehicle',
];

const EQUIPMENT_CLASSES = ['Straight Truck', 'Truck-Tractor', 'Semi-Trailers', 'Doubles/Triples', 'Bus', 'Other'];

const RadioGroup = ({ value, onChange }) => (
  <div className="flex items-center gap-4">
    {['Pass', 'Fail', 'N/A'].map(opt => (
      <label key={opt} className="flex items-center gap-1.5 cursor-pointer">
        <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${value === opt ? 'border-primary bg-primary' : 'border-border'}`}>
          {value === opt && <div className="w-1.5 h-1.5 rounded-full bg-primary-foreground" />}
        </div>
        <input type="radio" className="hidden" checked={value === opt} onChange={() => onChange(opt)} />
        <span className="text-xs text-foreground">{opt}</span>
      </label>
    ))}
  </div>
);

const SectionHeader = ({ title }) => (
  <div className="bg-white/5 px-3 py-1.5 rounded text-xs font-bold text-foreground uppercase tracking-wider mt-4 mb-3">
    {title}
  </div>
);

const Field = ({ label, required, children }) => (
  <div>
    <label className={labelCls}>{label}{required && <span className="text-red-400 ml-0.5">*</span>}</label>
    {children}
  </div>
);

export default function RoadTestCertificateModal({ driver, onClose, onSaved }) {
  const nameParts = (driver.full_name || '').trim().split(' ');
  const addrParts = (driver.address || '').split(',');
  const street = (addrParts[0] || '').trim();
  const city = (addrParts[1] || '').trim();
  const stateZip = (addrParts[2] || '').trim().split(' ');

  const [form, setForm] = useState({
    driver_name: driver.full_name || '',
    ssn: driver.ein || '',
    license_number: driver.cdl_number || '',
    license_state: driver.cdl_state || '',
    power_unit_type: 'Truck',
    trailer_type: driver.truck_type || '',
    bus_type: 'None',
    test_date: new Date().toISOString().split('T')[0],
    approx_miles: '',
    // Driver address
    address: street,
    city: city,
    state: stateZip[0] || driver.cdl_state || '',
    zip: stateZip[1] || '',
    phone: driver.phone || '',
    // Examiner info
    examiner_address: '6041 Thorne Bay Ct',
    examiner_city: 'Las Vegas',
    examiner_state: 'NV',
    examiner_zip: '89110',
    examiner_org: 'GF Trucklines LLC',
    examiner_title: 'Manager',
    examiner_name: '',
    examiner_signature: '',
    sign_date: new Date().toISOString().split('T')[0],
    // Ratings
    ratings: Object.fromEntries(RATING_ITEMS.map(item => [item, 'Pass'])),
    other_explain: '',
    remarks: '',
    // Equipment table
    equipment: Object.fromEntries(EQUIPMENT_CLASSES.map(cls => [cls, { type: '', hazmat: '', miles: '' }])),
  });

  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setRating = (item, val) => setForm(p => ({ ...p, ratings: { ...p.ratings, [item]: val } }));
  const setEquip = (cls, field, val) => setForm(p => ({
    ...p,
    equipment: { ...p.equipment, [cls]: { ...p.equipment[cls], [field]: val } }
  }));

  const generatePdf = async () => {
    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const W = 612, ml = 48, mr = 564, lh = 14;
    let y = 48;

    const txt = (str, x, yt, opts = {}) => {
      doc.setFontSize(opts.size || 9);
      doc.setFont('helvetica', opts.bold ? 'bold' : 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text(String(str || ''), x, yt, opts.align ? { align: opts.align } : {});
    };
    const drawLine = (y1) => { doc.setDrawColor(180, 180, 180); doc.line(ml, y1, mr, y1); };
    const sectionBg = (yy, h = 16) => {
      doc.setFillColor(220, 220, 220);
      doc.rect(ml, yy - 11, mr - ml, h, 'F');
    };
    const underlined = (label, value, x, yy, w = 120) => {
      doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(0, 0, 0);
      const lbl = `${label}: `;
      doc.text(lbl, x, yy);
      const lw = doc.getTextWidth(lbl);
      doc.text(String(value || ''), x + lw, yy);
      doc.line(x + lw, yy + 1, x + lw + w, yy + 1);
    };

    // Header
    txt('GF Trucklines LLC', ml, y, { bold: true, size: 12 });
    txt('GF Trucklines LLC', mr, y, { bold: true, align: 'right', size: 9 }); y += lh;
    txt('6041 Thorne Bay Ct', mr, y, { align: 'right', size: 9 }); y += lh;
    txt('Las Vegas, NV 89110', mr, y, { align: 'right', size: 9 }); y += lh;
    txt('(702) 601-8300', mr, y, { align: 'right', size: 9 }); y += 28;

    txt("CERTIFICATE OF DRIVER'S ROAD TEST", W / 2, y, { bold: true, size: 14, align: 'center' }); y += 20;

    doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(0, 0, 0);
    const instrText = doc.splitTextToSize(
      'Instructions: If the road test is successfully completed, the person who gave it shall complete a certificate of the driver\'s road test. The original or copy of the certificate shall be retained in the employing motor carrier\'s driver qualification file of the person examined and a copy given to the person who was examined. (49 CFR 391.31(e)(f)(g))',
      mr - ml
    );
    doc.text(instrText, ml, y); y += instrText.length * lh + 10;

    sectionBg(y); txt('CERTIFICATION OF ROAD TEST', ml + 2, y, { bold: true, size: 9 }); y += 18;
    underlined('Driver Name', form.driver_name, ml, y, 130);
    underlined('SSN', form.ssn, ml + 290, y, 100); y += lh + 4;
    underlined('License Number', form.license_number, ml, y, 120);
    underlined('License State', form.license_state, ml + 290, y, 80); y += lh + 4;
    underlined('Power Unit Type', form.power_unit_type, ml, y, 100);
    underlined('Trailer Type(s)', form.trailer_type, ml + 290, y, 100); y += lh + 4;
    underlined('If passenger carrier, type of bus', form.bus_type, ml, y, 100); y += lh + 8;

    doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(0, 0, 0);
    const certText = doc.splitTextToSize(
      `This is to certify that the above-named driver was given a road test under my supervision on the date of ${form.test_date}, consisting of approximately ${form.approx_miles || '__'} miles of driving. It is my considered opinion that this driver possesses sufficient driving skill to operate safely the type of commercial motor vehicle listed above.`,
      mr - ml
    );
    doc.text(certText, ml, y); y += certText.length * lh + 10;

    // Rating table
    sectionBg(y); txt('RATING OF PERFORMANCE', ml + 2, y, { bold: true, size: 9 }); y += 18;
    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    for (const item of RATING_ITEMS) {
      const rating = form.ratings[item] || 'Pass';
      doc.text(item, ml, y);
      doc.text(rating, mr, y, { align: 'right' });
      y += lh + 2;
    }
    if (form.other_explain) {
      doc.text(`Other: ${form.other_explain}`, ml, y); y += lh + 4;
    }
    y += 6;

    // Equipment table
    doc.addPage(); y = 48;
    sectionBg(y); txt('TYPE OF EQUIPMENT USED IN GIVING TEST', ml + 2, y, { bold: true, size: 9 }); y += 18;
    doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(0, 0, 0);
    doc.text('Equipment Class', ml, y);
    doc.text('Equipment Type', ml + 140, y);
    doc.text('Hazmat Y/N', ml + 310, y);
    doc.text('Approx Miles', ml + 430, y); y += lh + 4;
    doc.setFont('helvetica', 'normal');
    for (const cls of EQUIPMENT_CLASSES) {
      const eq = form.equipment[cls];
      doc.text(cls, ml, y);
      doc.text(eq.type || '', ml + 140, y);
      doc.text(eq.hazmat || '', ml + 310, y);
      doc.text(eq.miles || '', ml + 430, y);
      y += lh + 2;
    }
    y += 10;

    if (form.remarks) {
      doc.setFont('helvetica', 'bold'); doc.text('Remarks:', ml, y); y += lh;
      doc.setFont('helvetica', 'normal');
      const rm = doc.splitTextToSize(form.remarks, mr - ml);
      doc.text(rm, ml, y); y += rm.length * lh + 10;
    }

    // Examiner info
    sectionBg(y); txt('EXAMINER INFORMATION', ml + 2, y, { bold: true, size: 9 }); y += 18;
    underlined('Address', form.examiner_address, ml, y, 120);
    underlined('City', form.examiner_city, ml + 290, y, 100); y += lh + 4;
    underlined('State', form.examiner_state, ml, y, 40);
    underlined('Postal Code', form.examiner_zip, ml + 200, y, 80); y += lh + 4;
    underlined('Examiner organization', form.examiner_org, ml, y, 130);
    underlined('Examiner Title', form.examiner_title, ml + 290, y, 100); y += 40;

    // Signature
    if (form.examiner_signature && form.examiner_signature.startsWith('data:')) {
      doc.addImage(form.examiner_signature, 'PNG', ml, y, 100, 40);
    }
    doc.line(ml, y + 45, ml + 150, y + 45);
    doc.text('Examiner Signature', ml, y + 56);
    doc.text(form.sign_date, ml + 300, y + 50);
    doc.line(ml + 230, y + 45, ml + 400, y + 45);
    doc.text('Date Signed', ml + 300, y + 56);

    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8); doc.setTextColor(100);
      doc.text(new Date().toLocaleDateString(), ml, 780);
      doc.text('GF Trucklines LLC', W / 2, 780, { align: 'center' });
      doc.text(`Page ${i}`, mr, 780, { align: 'right' });
    }

    return doc;
  };

  const handleSave = async () => {
    if (!form.examiner_signature) {
      toast.error('Please provide an examiner signature.');
      return;
    }
    setSaving(true);
    toast.info('Generating Road Test Certificate PDF...');
    const doc = await generatePdf();
    const blob = doc.output('blob');
    const file = new File([blob], `road_test_cert_${(driver.full_name || 'driver').replace(/\s+/g, '_')}.pdf`, { type: 'application/pdf' });
    const { file_url } = await client.integrations.Core.UploadFile({ file });
    await client.entities.DriverProfile.update(driver.id, {
      dqf_road_test: true,
    });
    toast.success('Road Test Certificate saved!');
    setSaving(false);
    if (onSaved) onSaved(file_url);
    onClose();
  };

  const handleDownload = async () => {
    const doc = await generatePdf();
    doc.save(`road_test_cert_${(driver.full_name || 'driver').replace(/\s+/g, '_')}.pdf`);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-3xl z-10 max-h-[92vh] flex flex-col overflow-hidden">

        <div className="flex items-start justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div>
            <h2 className="font-space font-bold text-foreground text-lg">Certificate of Driver's Road Test</h2>
            <p className="text-xs text-muted-foreground mt-0.5">49 CFR 391.31(e)(f)(g)</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground mt-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto p-6 space-y-4 flex-1 text-sm">

          <SectionHeader title="Certification of Road Test" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Driver Name" required><input value={form.driver_name} onChange={e => set('driver_name', e.target.value)} className={inputCls} /></Field>
            <Field label="SSN"><input value={form.ssn} onChange={e => set('ssn', e.target.value)} placeholder="xxx-xx-0000" className={inputCls} /></Field>
            <Field label="License Number"><input value={form.license_number} onChange={e => set('license_number', e.target.value)} className={inputCls} /></Field>
            <Field label="License State">
              <select value={form.license_state} onChange={e => set('license_state', e.target.value)} className={inputCls}>
                <option value="" className="bg-background">Select…</option>
                {US_STATES.map(s => <option key={s} value={s} className="bg-background">{s}</option>)}
              </select>
            </Field>
            <Field label="Power Unit Type"><input value={form.power_unit_type} onChange={e => set('power_unit_type', e.target.value)} placeholder="e.g. Truck" className={inputCls} /></Field>
            <Field label="Trailer Type(s)"><input value={form.trailer_type} onChange={e => set('trailer_type', e.target.value)} placeholder="e.g. Reefer" className={inputCls} /></Field>
            <Field label="If passenger carrier, type of bus"><input value={form.bus_type} onChange={e => set('bus_type', e.target.value)} placeholder="None" className={inputCls} /></Field>
            <Field label="Date of Road Test" required><input type="date" value={form.test_date} onChange={e => set('test_date', e.target.value)} className={inputCls} /></Field>
            <Field label="Approx Miles"><input value={form.approx_miles} onChange={e => set('approx_miles', e.target.value)} placeholder="e.g. 25" className={inputCls} /></Field>
          </div>

          {/* Driver address */}
          <SectionHeader title="Driver Address" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Name" required><input value={form.driver_name} onChange={e => set('driver_name', e.target.value)} className={inputCls} /></Field>
            <Field label="Phone Number" required><input value={form.phone} onChange={e => set('phone', e.target.value)} className={inputCls} /></Field>
            <TrimbleAddressAutocomplete
              value={form.address}
              onChange={e => set('address', e.target.value)}
              onSelect={data => {
                set('address', data.street || form.address);
                if (data.city) set('city', data.city);
                if (data.state) set('state', data.state);
                if (data.zip) set('zip', data.zip);
              }}
              label="Address *"
            />
            <Field label="City" required><input value={form.city} onChange={e => set('city', e.target.value)} className={inputCls} /></Field>
            <Field label="State" required>
              <select value={form.state} onChange={e => set('state', e.target.value)} className={inputCls}>
                <option value="" className="bg-background">Select…</option>
                {US_STATES.map(s => <option key={s} value={s} className="bg-background">{s}</option>)}
              </select>
            </Field>
            <Field label="Postal Code" required><input value={form.zip} onChange={e => set('zip', e.target.value)} className={inputCls} /></Field>
          </div>

          {/* Rating of Performance */}
          <SectionHeader title="Rating of Performance" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            The road test shall be given by the motor carrier or a person designated by the motor carrier. <strong className="text-foreground">However, a driver who is a motor carrier must be given the test by another person.</strong>
          </p>
          <div className="space-y-2">
            {RATING_ITEMS.map(item => (
              <div key={item} className="flex items-center justify-between gap-3 py-2 border-b border-border">
                <span className="text-xs text-foreground flex-1">{item} <span className="text-red-400">*</span></span>
                <RadioGroup value={form.ratings[item]} onChange={val => setRating(item, val)} />
              </div>
            ))}
          </div>
          <Field label="Other, Explain:">
            <input value={form.other_explain} onChange={e => set('other_explain', e.target.value)} className={inputCls} />
          </Field>

          {/* Equipment table */}
          <SectionHeader title="Type of Equipment Used in Giving Test" />
          <div className="rounded-lg border border-border overflow-hidden text-xs">
            <div className="grid grid-cols-4 bg-white/5 px-3 py-2 font-semibold text-muted-foreground gap-2">
              <span>Equipment Class</span>
              <span>Equipment Type (Van, Flatbed, Tanker, Dump)</span>
              <span>Hazmat Y/N</span>
              <span>Approx Miles</span>
            </div>
            {EQUIPMENT_CLASSES.map(cls => (
              <div key={cls} className="grid grid-cols-4 gap-2 px-3 py-2 border-t border-border items-center">
                <span className="text-foreground">{cls}</span>
                <input value={form.equipment[cls].type} onChange={e => setEquip(cls, 'type', e.target.value)}
                  className="px-2 py-1 rounded bg-transparent border border-input text-foreground text-xs focus:outline-none focus:ring-1 focus:ring-primary/50" />
                <input value={form.equipment[cls].hazmat} onChange={e => setEquip(cls, 'hazmat', e.target.value)}
                  placeholder="Y / N"
                  className="px-2 py-1 rounded bg-transparent border border-input text-foreground text-xs focus:outline-none focus:ring-1 focus:ring-primary/50" />
                <input value={form.equipment[cls].miles} onChange={e => setEquip(cls, 'miles', e.target.value)}
                  className="px-2 py-1 rounded bg-transparent border border-input text-foreground text-xs focus:outline-none focus:ring-1 focus:ring-primary/50" />
              </div>
            ))}
          </div>

          <Field label="Remarks:">
            <textarea value={form.remarks} onChange={e => set('remarks', e.target.value)} rows={3}
              className={inputCls + ' resize-none'} />
          </Field>

          {/* Examiner info */}
          <SectionHeader title="Examiner Information" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Examiner Name" required><input value={form.examiner_name} onChange={e => set('examiner_name', e.target.value)} className={inputCls} /></Field>
            <Field label="Date of Road Test" required><input type="date" value={form.sign_date} onChange={e => set('sign_date', e.target.value)} className={inputCls} /></Field>
            <TrimbleAddressAutocomplete
              value={form.examiner_address}
              onChange={e => set('examiner_address', e.target.value)}
              onSelect={data => {
                set('examiner_address', data.street || form.examiner_address);
                if (data.city) set('examiner_city', data.city);
                if (data.state) set('examiner_state', data.state);
                if (data.zip) set('examiner_zip', data.zip);
              }}
              label="Address"
            />
            <Field label="City"><input value={form.examiner_city} onChange={e => set('examiner_city', e.target.value)} className={inputCls} /></Field>
            <Field label="State">
              <select value={form.examiner_state} onChange={e => set('examiner_state', e.target.value)} className={inputCls}>
                <option value="" className="bg-background">Select…</option>
                {US_STATES.map(s => <option key={s} value={s} className="bg-background">{s}</option>)}
              </select>
            </Field>
            <Field label="Postal Code"><input value={form.examiner_zip} onChange={e => set('examiner_zip', e.target.value)} className={inputCls} /></Field>
            <Field label="Examiner Organization"><input value={form.examiner_org} onChange={e => set('examiner_org', e.target.value)} className={inputCls} /></Field>
            <Field label="Examiner Title"><input value={form.examiner_title} onChange={e => set('examiner_title', e.target.value)} className={inputCls} /></Field>
          </div>

          <div>
            <label className={labelCls}>Examiner Signature <span className="text-red-400">*</span></label>
            <SignaturePad value={form.examiner_signature} onChange={v => set('examiner_signature', v)} />
          </div>

        </div>

        <div className="px-6 py-4 border-t border-border flex justify-between items-center gap-3 flex-shrink-0">
          <button type="button" onClick={handleDownload}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors">
            Download PDF (preview)
          </button>
          <div className="flex gap-3">
            <Button variant="outline" className="border-border" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-primary text-primary-foreground">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> Save & Upload</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}